import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { CONTACT } from "../data/content";
import { AiChatWidget } from "./AiChatWidget";

const PRIMARY_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/why-choose-us", label: "Why Us" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const MORE_LINKS = [
  { to: "/testimonials", label: "Testimonials" },
  { to: "/faqs", label: "FAQs" },
];

const FOOTER_EXPLORE = [{ to: "/", label: "Home" }, ...PRIMARY_LINKS.slice(0, 4), ...MORE_LINKS, PRIMARY_LINKS[4]];

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="site-logo" className="flex items-center gap-3 leading-none">
    <img
      src="/logo-emblem-gold.png"
      alt="Terraa & Title emblem"
      className="h-11 w-auto shrink-0"
    />
    <span className="flex flex-col">
      <span className={`font-accent text-xl font-medium tracking-wide ${dark ? "text-[#F4D387]" : "text-[#113835]"}`}>
        Terraa <span className="text-[#C1A74A]">&</span> Title
      </span>
      <span className={`text-[0.55rem] tracking-[0.3em] uppercase mt-1 whitespace-nowrap ${dark ? "text-white/60" : "text-[#C1A74A]"}`}>
        Land | Legacy | Lifestyle
      </span>
    </span>
  </Link>
);

export const WhatsAppIcon = ({ size = 22, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const WHATSAPP_URL = `https://wa.me/91${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi Terraa & Title, I need help with a property verification.")}`;

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="bg-[#113835]" data-testid="expert-topbar">
          <Link
            to="/join-experts"
            data-testid="expert-topbar-link"
            className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-center gap-2 text-[0.7rem] tracking-[0.14em] uppercase font-heading text-[#F4D387] hover:text-white transition-colors"
          >
            <span className="hidden sm:inline text-white/50">For advocates and surveyors —</span>
            <span className="font-medium text-white/80 hover:text-[#F4D387]">Join our expert network</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
        <header
          data-testid="site-header"
          className={`transition-colors duration-300 ${
            scrolled ? "bg-white/95 backdrop-blur-md border-b border-[#113835]/10 shadow-sm" : "bg-white/80 backdrop-blur-sm"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20 gap-6">
            <Logo />
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {PRIMARY_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `tt-link whitespace-nowrap font-heading text-[0.78rem] tracking-[0.12em] uppercase ${isActive ? "active text-[#113835] font-medium" : "text-[#1D504C]"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-4">
              <NavLink
                to="/join-experts"
                data-testid="nav-join-experts"
                className={({ isActive }) =>
                  `tt-btn-outline !py-2.5 !px-5 whitespace-nowrap ${isActive ? "bg-[#C1A74A] text-white" : ""}`
                }
              >
                Join as Experts
              </NavLink>
              <Link to="/request-title-search" data-testid="nav-cta-request" className="tt-btn-gold !py-2.5 !px-5 whitespace-nowrap">
                Request Verification
              </Link>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-[#113835] p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
            </button>
          </div>
          {open && (
            <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-[#113835]/10 px-6 pb-8 pt-4 space-y-4 max-h-[calc(100vh-7.25rem)] overflow-y-auto">
              {[{ to: "/", label: "Home" }, ...PRIMARY_LINKS, ...MORE_LINKS].map((l) => (
                <NavLink key={l.to} to={l.to} className="block font-heading text-sm uppercase tracking-widest text-[#113835] py-1">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/join-experts" data-testid="mobile-join-experts" className="tt-btn-outline w-full justify-center mt-4">
                Join as Experts
              </Link>
              <Link to="/request-title-search" className="tt-btn-gold w-full justify-center">
                Request Property Verification
              </Link>
            </div>
          )}
        </header>
      </div>

      <main className="flex-1 pt-[7.25rem]">
        <Outlet />
      </main>

      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        aria-label="Chat with us on WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
      >
        <WhatsAppIcon size={26} />
      </a>

      <AiChatWidget />

      <footer data-testid="site-footer" className="bg-[#113835] text-white tt-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-6">
            <Logo dark />
            <p className="text-white/70 text-sm leading-relaxed max-w-md font-light">
              Expert-reviewed property verification, land records retrieval, and due diligence —
              focused on Maharashtra. Trusted by buyers, agents, law firms, and lenders.
            </p>
            <p className="text-white/40 text-xs font-light">
              Terraa & Title Private Limited
            </p>
          </div>
          <div>
            <h3 className="font-heading text-xs tracking-[0.24em] uppercase text-[#C1A74A] mb-5">Explore</h3>
            <ul className="space-y-3 text-sm text-white/80 font-light">
              {FOOTER_EXPLORE.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-[#F4D387] transition-colors">{l.label}</Link>
                </li>
              ))}
              <li>
                <Link to="/request-title-search" className="text-[#F4D387] hover:text-white transition-colors">Request Property Verification</Link>
              </li>
              <li>
                <Link to="/pricing" data-testid="footer-pricing" className="text-[#F4D387] hover:text-white transition-colors">Pricing</Link>
              </li>
              <li>
                <Link to="/sample-report" data-testid="footer-sample-report" className="text-[#F4D387] hover:text-white transition-colors">Sample Report</Link>
              </li>
              <li>
                <Link to="/ask-terraa" data-testid="footer-ask-terraa" className="text-[#F4D387] hover:text-white transition-colors">Ask Terraa AI</Link>
              </li>
              <li>
                <Link to="/due-diligence-tracker" data-testid="footer-tracker" className="text-[#F4D387] hover:text-white transition-colors">Due Diligence Tracker</Link>
              </li>
              <li>
                <Link to="/join-experts" data-testid="footer-join-experts" className="text-[#F4D387] hover:text-white transition-colors">Join as Experts</Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-xs tracking-[0.24em] uppercase text-[#C1A74A] mb-5">Contact</h3>
            <ul className="space-y-4 text-sm text-white/80 font-light">
              <li className="flex items-start gap-3">
                <Phone size={16} strokeWidth={1.25} className="mt-0.5 text-[#C1A74A]" />
                <a href={`tel:${CONTACT.phoneRaw}`} data-testid="footer-phone" className="hover:text-[#F4D387] transition-colors">{CONTACT.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} strokeWidth={1.25} className="mt-0.5 text-[#C1A74A]" />
                <span data-testid="footer-email" className="break-all">{CONTACT.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.25} className="mt-0.5 text-[#C1A74A]" />
                <span>{CONTACT.address}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-2">
            <p data-testid="footer-disclaimer" className="text-[0.68rem] text-white/40 font-light leading-relaxed max-w-4xl">
              Disclaimer: Terraa & Title reports and Land Ratings are expert summaries based on documents and records
              available and reviewed at the time of review. They are not title insurance, not a legal guarantee, and not
              a substitute for independent legal advice where required.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50 font-light">
            <span>© {new Date().getFullYear()} Terraa & Title Private Limited. All rights reserved.</span>
            <div className="flex items-center gap-5">
              <Link to="/terms" data-testid="footer-terms" className="hover:text-[#C1A74A] transition-colors">Terms of Use</Link>
              <Link to="/refund-policy" data-testid="footer-refund" className="hover:text-[#C1A74A] transition-colors">Refund Policy</Link>
              <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[#C1A74A] transition-colors">Admin Login</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
